import React, { useEffect } from 'react';
import { TextField } from '@material-ui/core';
import {
  Grid, Avatar, Button, Card, Box, CardContent, IconButton
} from '@mui/material';
import {
  Formik, Form, FormikProps, Field
} from 'formik';
import * as Yup from 'yup';
import { Autocomplete as FormikAutocomplete } from 'formik-material-ui-lab';
import { Autocomplete, AutocompleteRenderInputParams } from '@material-ui/lab';
import _ from 'lodash';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { pdf } from '@react-pdf/renderer';
import saveAs from 'file-saver';

import { useAuth } from '../../../../contexts/auth';
import WrapperSimple from '../../../../layout-components/ExampleWrapperSimple';
import Dictionary from '../../../../models/interfaces/dictionary';
import customerService from '../../../../services/customerService';
import callService from '../../../../services/callService';
import NoData from '../../../../components/noData';
import driverService from '../../../../services/driverService';
import ExportCSV from '../../../../components/exportExcel';
import PaymentType, { PaymentTypeList, PaymentTypeText } from '../../../../models/enums/paymentType';
import { toBrazilDateTime } from '../../../../components/mask';
import { getFirstDate, getLastDate } from '../../../../utils/dateHelper';
import Loading from '../../../../components/loading';

import GeneralPrint from './generalPrint';
import DefaultPrint, { RowReport } from './defaultPrint';

interface Formulario {
  client: Dictionary | null;
  driver: Dictionary | null;
  paymentType: Dictionary | null;
  startDate: Date | null;
  endDate: Date | null;
}

interface ClientRow {
  id: string;
  name: string;
  calls: any[];
}

interface Row {
  id: string;
  description: string;
  photoURL: string;
  group: ClientRow[];
  faturadoAmount: number;
  presencialAmount: number;
  callsTotal: number;
  total: number;
}

interface OwnProps {
  subsidiaryId: string;
}

const allOption: Dictionary = { key: null, value: 'Todos' };

const stripHtml = (value: string | null | undefined) => (value ?? '').replace(/<[^>]*>/g, '');

const page = (own: OwnProps) => {
  let { subsidiaryId } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setCalls] = React.useState<Row[]>();
  const [clients, setClients] = React.useState<Dictionary[]>([]);
  const [drivers, setDrivers] = React.useState<Dictionary[]>([]);
  const [valuesForm, setValuesForm] = React.useState<Formulario>();
  const [isLoading, setIsLoading] = React.useState(false);
  const faturadoList = [PaymentType.Faturado, PaymentType.Prepago];
  if (own.subsidiaryId) subsidiaryId = own.subsidiaryId;

  const getSubsidiaryFilter = () => (subsidiaryId ? `subsidiary:${subsidiaryId}` : null);

  const searchClients = React.useCallback(async (params: string = '') => {
    const result = await customerService.getListForAutocomplete(subsidiaryId, params);
    const options = result.hits.map((item: any) => ({
      key: item.objectID,
      value: stripHtml(item.name ?? item.businessName ?? item.responsible)
    }));
    setClients([allOption, ...options]);
  }, [subsidiaryId]);

  const searchDrivers = React.useCallback(async (params: string = '') => {
    const result = await driverService.search(params, 20, 0, getSubsidiaryFilter());
    const options = result.items.map((item: any) => ({
      key: item.id,
      value: stripHtml(item.name)
    }));
    setDrivers([allOption, ...options]);
  }, [subsidiaryId]);

  useEffect(() => {
    searchClients();
    searchDrivers();
  }, [searchClients, searchDrivers]);

  const calculateDestinationsInfo = (list: any[]) => {
    const destinations = list.map((x) => x.address).join(' e ');
    const distanceTotal = (_.sumBy(list, (x) => x.distance) / 1000).toFixed(2);

    return { destinations, distanceTotal };
  };

  const generateReportToAllxAll = () => {
    const resultRows: any[] = [];
    rows?.forEach((row) => {
      const payments = _.map(_.groupBy(row.group, 'payment.type'), (calls: any, paymentId: string) => ({
        type: parseInt(paymentId, 10),
        total: _.sumBy(calls, 'payment.value')
      }));

      resultRows.push({
        Id: row.id,
        Cliente: row.description,
        Dinheiro: _.find(payments, (x: any) => x.type === PaymentType.Money)?.total ?? 0,
        Debito: _.find(payments, (x: any) => x.type === PaymentType.Debit)?.total ?? 0,
        Credito: _.find(payments, (x: any) => x.type === PaymentType.Credit)?.total ?? 0,
        Pix: _.find(payments, (x: any) => x.type === PaymentType.Pix)?.total ?? 0,
        Picpay: _.find(payments, (x: any) => x.type === PaymentType.Picpay)?.total ?? 0,
        Faturado: _.find(payments, (x: any) => faturadoList.includes(x.type))?.total ?? 0,
        Total: _.sumBy(payments, 'total')
      });
    });

    resultRows.push({
      Id: uuidv4(),
      Cliente: 'TOTAL',
      Dinheiro: _.sumBy(resultRows, 'Dinheiro'),
      Debito: _.sumBy(resultRows, 'Debito'),
      Credito: _.sumBy(resultRows, 'Credito'),
      Pix: _.sumBy(resultRows, 'Pix'),
      Picpay: _.sumBy(resultRows, 'Picpay'),
      Faturado: _.sumBy(resultRows, 'Faturado'),
      Total: _.sumBy(resultRows, 'Total')
    });

    return resultRows;
  };

  const generateReportByDriver = () => {
    let resultRows: RowReport[] = [];
    rows?.forEach((row) => {
      row.group?.forEach((call: any) => {
        const { destinations, distanceTotal } = calculateDestinationsInfo(call.destinations);

        resultRows.push({
          Id: call.id,
          Date: toBrazilDateTime(call.createdAt),
          Condutor: call.driver.name,
          Cliente: call.origin.responsible,
          HasBack: call.hasBack,
          Destinos: destinations,
          Distancia: `${distanceTotal} km`,
          Pagamento: PaymentTypeText[parseInt(call.payment.type, 10) as PaymentType],
          Payment: call.payment,
          Total: call.payment.value
        });
      });
    });
    resultRows = _.sortBy(resultRows, (x) => x.Date);

    resultRows.push({
      Id: uuidv4(),
      Date: '',
      Condutor: '',
      Cliente: '',
      Destinos: '',
      Distancia: '',
      Pagamento: 'TOTAL',
      HasBack: false,
      Payment: null,
      Total: _.sumBy(resultRows, 'Total')
    });

    return resultRows;
  };

  const generateReportByClient = () => {
    let resultRows: RowReport[] = [];

    rows?.forEach((row) => {
      row.group?.forEach((client: any) => {
        client.calls?.forEach((call: any) => {
          const { destinations, distanceTotal } = calculateDestinationsInfo(call.destinations);

          if (!call.driver) {
            console.log(`Call(${call.id}) with driver not found`);
          }

          resultRows.push({
            Id: call.id,
            Date: toBrazilDateTime(call.createdAt),
            Cliente: call.origin.responsible,
            Condutor: call.driver?.name ?? '?',
            HasBack: call.hasBack,
            Destinos: destinations,
            Distancia: `${distanceTotal} km`,
            Pagamento: PaymentTypeText[parseInt(call.payment.type, 10) as PaymentType],
            Payment: call.payment,
            Total: call.payment.value
          });
        });
      });
    });
    resultRows = _.sortBy(resultRows, (x) => x.Date);
    resultRows.push({
      Id: uuidv4(),
      Date: '',
      Cliente: '',
      Condutor: '',
      Destinos: '',
      Distancia: '',
      Pagamento: 'TOTAL',
      HasBack: false,
      Payment: null,
      Total: _.sumBy(resultRows, 'Total')
    });

    return resultRows;
  };

  const getRowsToReport = () => {
    if (valuesForm?.client?.key === null && valuesForm?.driver?.key === null) {
      return generateReportToAllxAll();
    } if (valuesForm?.client?.key === null && valuesForm?.driver?.key !== null) {
      return generateReportByDriver();
    }
    return generateReportByClient();
  };

  const getPDF = () => {
    const data = getRowsToReport();
    if (valuesForm?.client?.key === null && valuesForm?.driver?.key === null) {
      return (
        <GeneralPrint
          data={data}
          fileName={format(new Date(), 'yyyyMMdd_hhmmss')}
          startDate={valuesForm?.startDate}
          endDate={valuesForm?.endDate}
        />
      );
    }

    return (
      <DefaultPrint
        data={data}
        fileName={format(new Date(), 'yyyyMMdd_hhmmss')}
        startDate={valuesForm?.startDate}
        endDate={valuesForm?.endDate}
      />
    );
  };

  const getDataByClient = (report: any[]) => {
    const callsByClient = _.groupBy(report, 'origin.clientId');
    let result = _.map(callsByClient, (calls: any[], clientId: string) => ({
      id: clientId,
      description: calls[0].origin.responsible,
      photoURL: '',
      group: calls,
      faturadoAmount: _.sumBy(calls.filter((w) => faturadoList.includes(w.payment.type)), (x) => x.payment.value),
      presencialAmount: _.sumBy(calls.filter((w) => !faturadoList.includes(w.payment.type)), (x) => x.payment.value),
      callsTotal: callService.getDestinationsCount(calls),
      total: _.sumBy(calls, 'payment.value')
    }));
    result = _.sortBy(result, (x) => x.description);
    result.push({
      id: uuidv4(),
      description: 'Total',
      photoURL: '---',
      group: [],
      faturadoAmount: _.sumBy(result, (x) => x.faturadoAmount),
      presencialAmount: _.sumBy(result, (x) => x.presencialAmount),
      callsTotal: _.sumBy(result, 'callsTotal'),
      total: _.sumBy(result, 'total')
    });
    setCalls(result);
  };

  const getDataByDriver = (report: any[]) => {
    const reportByDriver = _.groupBy(report, 'driver.id');
    let result = _.map(reportByDriver, (driver, driverId) => {
      const callsByClient = _.groupBy(driver, 'origin.clientId');
      const clientsResult = _.map(callsByClient, (calls: any[], clientId: string) => ({
        id: clientId,
        name: calls[0].origin.responsible,
        calls,
        faturadoAmount: _.sumBy(calls.filter((w) => faturadoList.includes(w.payment.type)), (x) => x.payment.value),
        presencialAmount: _.sumBy(calls.filter((w) => !faturadoList.includes(w.payment.type)), (x) => x.payment.value),
        length: callService.getDestinationsCount(calls),
        total: _.sumBy(calls, 'payment.value')
      }));

      if (!driver[0].driver) {
        console.log(`Driver(${driverId}) not found`);
      }

      return {
        id: driverId,
        description: driver[0].driver?.name ?? '?',
        photoURL: driver[0].driver?.photo ?? '?',
        group: clientsResult,
        faturadoAmount: _.sumBy(clientsResult, (x) => x.faturadoAmount),
        presencialAmount: _.sumBy(clientsResult, (x) => x.presencialAmount),
        callsTotal: _.sumBy(clientsResult, 'length'),
        total: _.sumBy(clientsResult, 'total')
      };
    });
    result = _.sortBy(result, (x) => x.description);
    result.push({
      id: uuidv4(),
      description: 'Total',
      photoURL: '---',
      group: [],
      faturadoAmount: _.sumBy(result, 'faturadoAmount'),
      presencialAmount: _.sumBy(result, 'presencialAmount'),
      callsTotal: _.sumBy(result, 'callsTotal'),
      total: _.sumBy(result, 'total')
    });
    setCalls(result);
  };

  return (
    <>
      <WrapperSimple sectionHeading="Relatório de receita">
        <Formik
          initialValues={{
            client: { key: null, value: 'Todos' },
            driver: { key: null, value: 'Todos' },
            paymentType: { key: null, value: 'Todos' },
            startDate: getFirstDate(),
            endDate: getLastDate()
          }}
          validationSchema={Yup.object().shape({
            client: Yup.object().required().nullable(),
            driver: Yup.object().required().nullable(),
            paymentType: Yup.object().required().nullable(),
            startDate: Yup.date().required().nullable(),
            endDate: Yup.date().required().nullable()
          })}
          onSubmit={async (values: Formulario, actions) => {
            setIsLoading(true);
            values.endDate?.setSeconds(59);
            setValuesForm(values);

            callService.getReport(subsidiaryId, values.startDate!, values.endDate!, values.client?.key, values.driver?.key, values.paymentType?.key).then((report) => {
              if (values.client?.key === null && values.driver?.key === null) {
                getDataByClient(report);
              } else if (values.client?.key === null && values.driver?.key !== null) {
                getDataByClient(report);
              } else {
                getDataByDriver(report);
              }
            }).catch((e) => {
              enqueueSnackbar(e.message, { variant: 'error' });
            }).finally(() => {
              actions.setSubmitting(false);
              setIsLoading(false);
            });
          }}
        >
          {(props: FormikProps<Formulario>) => {
            const {
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
              setFieldValue
            } = props;

            return (
              <Form noValidate autoComplete="off">
                <Field type="hidden" name="address.ibge" value="" />
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      value={values.client}
                      options={clients}
                      getOptionLabel={(option: Dictionary) => option.value}
                      getOptionSelected={(option: Dictionary, value: Dictionary) => option.key === value.key}
                      onChange={(_event, value) => setFieldValue('client', value)}
                      onInputChange={(_event, value, reason) => {
                        if (reason === 'input' || reason === 'clear') searchClients(value);
                      }}
                      onOpen={() => searchClients()}
                      renderInput={(params: AutocompleteRenderInputParams) => (
                        <TextField
                          {...params}
                          fullWidth
                          required
                          variant="outlined"
                          label="Cliente"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          autoFocus
                          error={Boolean(errors.client && touched.client)}
                          helperText={errors.client && touched.client && errors.client}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      value={values.driver}
                      options={drivers}
                      getOptionLabel={(option: Dictionary) => option.value}
                      getOptionSelected={(option: Dictionary, value: Dictionary) => option.key === value.key}
                      onChange={(_event, value) => setFieldValue('driver', value)}
                      onInputChange={(_event, value, reason) => {
                        if (reason === 'input' || reason === 'clear') searchDrivers(value);
                      }}
                      onOpen={() => searchDrivers()}
                      renderInput={(params: AutocompleteRenderInputParams) => (
                        <TextField
                          {...params}
                          fullWidth
                          required
                          variant="outlined"
                          label="Entregador(a)"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(errors.driver && touched.driver)}
                          helperText={errors.driver && touched.driver && errors.driver}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Field
                      name="paymentType"
                      component={FormikAutocomplete}
                      options={PaymentTypeList}
                      getOptionLabel={(option: Dictionary) => option.value}
                      renderInput={(params: AutocompleteRenderInputParams) => (
                        <TextField
                          {...params}
                          fullWidth
                          required
                          variant="outlined"
                          label="Formas de Pagamento"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(errors.paymentType && touched.paymentType)}
                          helperText={errors.paymentType && touched.paymentType && errors.paymentType}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={4}>
                    <KeyboardDateTimePicker
                      fullWidth
                      required
                      inputVariant="outlined"
                      label="Início"
                      format="dd/MM/yyyy HH:mm"
                      ampm={false}
                      InputLabelProps={{ shrink: true }}
                      name="startDate"
                      onChange={(date: Date | null) => { setFieldValue('startDate', date); }}
                      onBlur={handleBlur}
                      value={values.startDate}
                      error={Boolean(errors.startDate && touched.startDate)}
                      helperText={errors.startDate && touched.startDate && errors.startDate}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={4}>
                    <KeyboardDateTimePicker
                      fullWidth
                      required
                      inputVariant="outlined"
                      label="Fim"
                      format="dd/MM/yyyy HH:mm"
                      ampm={false}
                      InputLabelProps={{ shrink: true }}
                      name="endDate"
                      onChange={(date: Date | null) => { setFieldValue('endDate', date); }}
                      onBlur={handleBlur}
                      value={values.endDate}
                      error={Boolean(errors.endDate && touched.endDate)}
                      helperText={errors.endDate && touched.endDate && errors.endDate}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={4}>
                    <Button variant="contained" color="primary" type="submit" disabled={isSubmitting} fullWidth>
                      <span className="btn-wrapper--icon">
                        <FontAwesomeIcon icon={['far', 'keyboard']} />
                      </span>
                      <span className="btn-wrapper--label">Buscar</span>
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </WrapperSimple>
      {isLoading ? <Loading /> : (
        <>
          {
            rows && (
              rows.length > 0 ? (
                <Card className="card-box mb-4">
                  <div className="card-header">
                    <div className="card-header--title" />
                    <Box className="card-header--actions">
                      {' | '}
                      <IconButton
                        size="small"
                        color="primary"
                        className="text-primary"
                        title="Exportar para PDF"
                        onClick={async () => {
                          const blob = await pdf(getPDF()).toBlob();
                          saveAs(blob, format(new Date(), 'yyyyMMdd_hhmmss'));
                        }}
                      >
                        <FontAwesomeIcon icon={['far', 'file-pdf']} className="font-size-lg" />
                      </IconButton>
                      {' | '}
                      <ExportCSV csvData={getRowsToReport()} fileName={format(new Date(), 'yyyyMMdd_hhmmss')} />
                    </Box>
                  </div>
                  <CardContent className="p-0">
                    <div className="table-responsive">
                      <table className="text-nowrap mb-0 table table-borderless table-hover">
                        <thead>
                          <tr>
                            <th className="text-left">Descrição</th>
                            <th className="text-right">Entregas</th>
                            <th className="text-right" title="Valores recebidos pela franquia (Faturado ou Pré-pago)">Faturado</th>
                            <th className="text-right" title="Valores recebidos diretamente pelo condutor">Presencial</th>
                            <th className="text-right">Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((x) => (
                            <tr key={x.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  {x.photoURL !== '---' && <Avatar alt="" src={x.photoURL} className="mr-2" />}
                                  <div>
                                    <a
                                      href="#/"
                                      onClick={(e) => e.preventDefault()}
                                      className="font-weight-bold text-black"
                                      title=""
                                    >
                                      {x.description}
                                    </a>
                                  </div>
                                </div>
                              </td>
                              <td className="text-right">
                                {x.callsTotal}
                              </td>
                              <td className="text-right">
                                {x.faturadoAmount?.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                              </td>
                              <td className="text-right">
                                {x.presencialAmount?.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                              </td>
                              <td className="text-right">
                                {x.total?.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ) : <NoData message="Nenhuma informação encontrada." />
            )
          }
        </>
      )}
    </>
  );
};

export default page;
